import * as Cesium from 'cesium'
import { getRock5aDeviceSession } from './rock5aDeviceSession.js'
import { connectionContextToDevice } from './rock5aConnectionContext.js'

// 手势码定义:
//  1 = 张开手掌(光标移动)   2 = 捏合(点击)
//  3 = 挥手(返回/关闭)     4 = 握拳(唤醒语音)
//  5 = 食指弯曲(滚轮)      6 = 胜利(关闭手势识别)
//  7 = 大拇指朝上(开启/确认) 8 = OK(确认)
//  9 = 双手捏合(缩放拖拽)  10 = 双手握拳(退出系统)
//  11 = 握拳+张开(语音激活)
//
// 系统开关: 大拇指朝上→开启手势识别, 胜利→关闭手势识别
// 开启后: 光标/点击/握拳/挥手/OK/食指弯曲/双手联动 全部可用
// 关闭后: 不显示光标, 不响应手势, 后端RKNN继续运行
const GESTURE_MAP = {
  '张开手掌':   { code: 1, name: '光标' },
  '捏合':       { code: 2, name: '点击' },
  '握拳':       { code: 4, name: '唤醒语音' },
  '挥手':       { code: 3, name: '返回' },
  '大拇指朝上': { code: 7, name: '开启/确认' },
  '食指弯曲':   { code: 5, name: '滚轮' },
  '胜利':       { code: 6, name: '关闭手势识别' },
  'OK':         { code: 8, name: '确认' },
  '无':         { code: 0, name: '' },
}

const GESTURE_MAP_EN = {
  'open_palm': { code: 1, name: '' },
  'pinch':     { code: 2, name: '点击' },
  'fist':      { code: 4, name: '握拳' },
  'wave':      { code: 3, name: '返回' },
  'thumb_up':  { code: 7, name: '开启/确认' },
  'thumbs_up': { code: 7, name: '开启/确认' },
  'ok':        { code: 8, name: '确认' },
  'five':      { code: 1, name: '' },
  'pointing_up': { code: 5, name: '滚轮' },
  'point':     { code: 5, name: '滚轮' },
  'index_curl': { code: 5, name: '滚轮' },
  'peace':     { code: 6, name: '关闭手势识别' },
  'none':      { code: 0, name: '' },
}

// 双手联动动作码
const COMBINED_MAP = {
  'scale_drag':     { code: 9,  name: '缩放拖拽' },
  'exit_system':    { code: 10, name: '退出系统' },
  'voice_activate': { code: 11, name: '语音激活' },
  'drag':           { code: 9,  name: '拖拽' },
  'none':           { code: 0,  name: '' },
}

const DEFAULT_MAGNETIC_SELECTORS = [
  '.clickable-area', 'button', '.el-button', '[role="button"]',
  '.menu-item', '.close-item', '.orb-core', '.el-select',
  '.el-input__inner', '.el-switch', 'input', 'a', 'area',
  '.close', '.el-dialog__headerbtn', '[onclick]',
  '.exit-l0-btn', '.enter-cockpit-btn', '.mic-icon', '.main-connect-btn',
  '.action-btn'
]

// 旧终端 payload 没有 confidence；此时按 1.0 处理以保持兼容。
const HAND_CONFIDENCE = Object.freeze({
  tracking: 0.45,
  neutral: 0.55,
  action: 0.70,
  combined: 0.75,
  critical: 0.82,
})

const TWO_HAND_DEADZONE = Object.freeze({
  scale: 0.008,
  rotate: 0.0015,
  tilt: 0.0025,
})

export class GestureService {
  constructor(options = {}) {
    this._ws = null
    this._session = options.session || getRock5aDeviceSession()
    this._connectionContext = null
    this._sessionConsumer = 'gesture-service'
    this._sessionUnsubscribers = []
    this._sessionAcquired = false
    this._isConnected = false
    this._cursorX = window.innerWidth / 2
    this._cursorY = window.innerHeight / 2
    this._targetX = window.innerWidth / 2
    this._targetY = window.innerHeight / 2
    this._currentGesture = 1
    this._gestureName = ''
    this._isSnapped = false
    this._isCursorActive = false
    this._cursorIdleTimer = null
    this._snapRadius = 100
    this._magneticSelectors = [...DEFAULT_MAGNETIC_SELECTORS]
    this._lastHoveredEl = null
    this._baseSmoothing = 0.08
    this._maxSmoothing = 0.45

    this._gestureCb = null
    this._actionCbs = []   // 支持多个动作回调 (App.vue + SmartOrb.vue 各注册一个)
    this._cesiumViewer = null
    this._cesium3DEnabled = false

    this._gestureTimer = null
    this._fistLock = false
    this._fistGraceTimer = null
    this._fistGraceMs = 120
    this._clickVisualTimer = null
    this._lastClickTime = 0
    this._pinchArmed = false
    this._neutralRequired = true
    this._neutralSince = 0
    this._neutralHoldMs = 300
    this._lastActionGesture = 0
    this._lastByeTime = 0
    this._lastConfirmTime = 0
    this._exitHoldTimer = null
    this._exitHoldMs = 1500
    this._voiceActivateHoldMs = 400
    this._exitArmed = true
    this._combinedActionFired = false
    this._scrollStartY = 0
    this._scrollLastY = 0
    this._isScrolling = false
    this._isClicking = false
    this._controlEnabled = false  // 来自 Rock5A payload 的 control_enabled
    this._primaryHand = 'right'
    this._legacyControlCandidate = 0
    this._legacyControlSince = 0
    this._lastCombinedGesture = 'none'
    this._feedbackTimes = new Map()
    // 双手缩放/拖拽状态
    this._lastScaleDist = 0
    this._isScaleDragging = false
    this._scaleDragCandidateSince = 0
    this._scaleDragLocked = false
    this._scaleDragDwellMs = 280
    // EMA 平滑器
    this._emaScaleRatio = 1.0     // 缩放比率 EMA
    this._twoHandCursorX = 0      // 双手光标平滑位置
    this._twoHandCursorY = 0
    this._twoHandInited = false
    // 滚动器：定时器方式持续滚动
    this._scrollAccumY = 0
    this._scrollTimer = null
    this._lastMidX = 0
    this._lastMidY = 0
    this._midInited = false
    this._emaDx = 0
    this._emaDy = 0
    this._twoHandMode = 'idle'
    this._twoHandModeCandidate = 'idle'
    this._twoHandModeCandidateSince = 0
    this._twoHandModeLockedAt = 0
    this._twoHandModeDwellMs = 180
    this._twoHandModeMinLockMs = 360
    this._twoHandIdleSince = 0

    this._bindSession()
  }

  connect(context, compatibilityDevice = null) {
    const device = compatibilityDevice
      || (context && context.controlWsUrl ? connectionContextToDevice(context) : context)
    if (!device) throw new Error('[GestureService] Rock5aConnectionContext is required')
    this._connectionContext = context && context.controlWsUrl ? context : null
    this._bindSession()
    this._sessionAcquired = true
    return this._session.connect(device, this._sessionConsumer)
  }

  connectPrimary(ip) {
    return this.connect({ ip, port: 8770 })
  }

  connectFallback(host) {
    return this.connect({ ip: host || 'localhost', port: 8766 })
  }

  disconnect() {
    if (this._sessionAcquired) this._session.release(this._sessionConsumer)
    this._sessionAcquired = false
    this._sessionUnsubscribers.forEach(unsubscribe => unsubscribe())
    this._sessionUnsubscribers = []
    this._ws = null
    this._connectionContext = null
    this._isConnected = false
    this._controlEnabled = false
    this.cancelPendingGesture('disconnect')
  }

  _bindSession() {
    if (this._sessionUnsubscribers.length > 0) return
    this._sessionUnsubscribers = [
      this._session.subscribe('gesture', payload => this._handleMessage(payload)),
      this._session.subscribe('status', status => this._handleSessionStatus(status))
    ]
    this._handleSessionStatus(this._session.status)
  }

  _handleSessionStatus(status) {
    const wasConnected = this._isConnected
    this._isConnected = Boolean(status && status.control === 'connected')
    this._ws = this._session.controlWs
    if (wasConnected && !this._isConnected) {
      this._controlEnabled = false
      this.cancelPendingGesture('connection-lost')
      if (this._gestureCb) {
        this._gestureCb({ code: 0, x: this._cursorX, y: this._cursorY, controlEnabled: false })
      }
    }
  }

  _normalizeHand(raw = {}, fallback = {}) {
    const hand = { ...fallback, ...raw }
    const parsedConfidence = Number(hand.confidence)
    const hasConfidence = raw.confidence !== undefined && raw.confidence !== null
    hand.confidence = hasConfidence && Number.isFinite(parsedConfidence)
      ? Math.max(0, Math.min(1, parsedConfidence))
      : (hand.is_active ? 1.0 : 0.0)
    hand.x = Number.isFinite(Number(hand.x)) ? Number(hand.x) : 0.5
    hand.y = Number.isFinite(Number(hand.y)) ? Number(hand.y) : 0.5
    hand.is_active = Boolean(hand.is_active)
    return hand
  }

  _screenX(value, coordinateSpace) {
    const ratio = Math.max(0, Math.min(1, Number(value) || 0))
    // v2 Rock5A 已把坐标转换为用户镜像空间；旧 payload 没有该字段，
    // 继续沿用历史翻转，保证新旧终端混用时光标方向都正确。
    const userFacing = coordinateSpace === 'user_mirror' || coordinateSpace === 'screen'
    return (userFacing ? ratio : 1.0 - ratio) * window.innerWidth
  }

  _confidenceTier(confidence) {
    if (confidence >= HAND_CONFIDENCE.action) return 'action'
    if (confidence >= HAND_CONFIDENCE.tracking) return 'tracking'
    return 'rejected'
  }

  _pointerFrameMetadata(payload, hand, handedness, pointerActive) {
    const rawTimestamp = Number(payload.timestamp || hand.sample_time)
    const timestamp = Number.isFinite(rawTimestamp) && rawTimestamp > 0
      ? (rawTimestamp < 10000000000 ? Math.round(rawTimestamp * 1000) : Math.round(rawTimestamp))
      : Date.now()
    return {
      gestureName: payload.gesture_name || hand.gesture || '无',
      confidence: hand.confidence,
      stableTrackId: payload.stable_track_id ?? hand.stable_track_id ?? handedness,
      handedness: payload.handedness || hand.handedness || handedness,
      timestamp,
      pointerActive: Boolean(pointerActive),
    }
  }

  _combinedConfidenceThreshold(combined) {
    if (combined === 'exit_system') return HAND_CONFIDENCE.critical
    if (combined === 'voice_activate') return 0.78
    return HAND_CONFIDENCE.combined
  }

  _handleMessage(payload) {
    if (!payload) return
    const inactiveHand = { gesture: '无', x: 0.5, y: 0.5, is_active: false, confidence: 0 }
    let right, left
    if (payload.right) {
      right = this._normalizeHand(payload.right, inactiveHand)
      left = this._normalizeHand(payload.left || {}, inactiveHand)
    } else {
      const gesture = payload.gesture || '无'
      right = this._normalizeHand({
        gesture,
        x: Number.isFinite(Number(payload.x)) ? Number(payload.x) : 0.5,
        y: Number.isFinite(Number(payload.y)) ? Number(payload.y) : 0.5,
        is_active: payload.is_active !== undefined ? Boolean(payload.is_active) : gesture !== '无',
        confidence: payload.confidence,
      }, inactiveHand)
      left = this._normalizeHand({}, inactiveHand)
    }

    if (payload.primary_hand === 'left' || payload.primary_hand === 'right') {
      this._primaryHand = payload.primary_hand
    }
    const preferredPrimary = this._primaryHand === 'left' ? left : right
    const alternatePrimary = this._primaryHand === 'left' ? right : left
    // 主手暂时离开画面时允许另一只手继续指向；
    // 高影响的开/关控制仍只由 Rock5A 指定的主手判定。
    const preferredTrackable = preferredPrimary.is_active
      && preferredPrimary.confidence >= HAND_CONFIDENCE.tracking
    const alternateTrackable = alternatePrimary.is_active
      && alternatePrimary.confidence >= HAND_CONFIDENCE.tracking
    const primary = preferredTrackable ? preferredPrimary : alternatePrimary
    const primaryHandedness = preferredTrackable
      ? this._primaryHand
      : (this._primaryHand === 'left' ? 'right' : 'left')
    const pointerFrame = this._pointerFrameMetadata(
      payload,
      primary,
      primaryHandedness,
      preferredTrackable || alternateTrackable,
    )

    const wasEnabled = this._controlEnabled
    if (payload.control_enabled !== undefined) {
      this._controlEnabled = Boolean(payload.control_enabled)
      this._legacyControlCandidate = 0
      this._legacyControlSince = 0
    } else {
      const legacyGesture = primary.confidence >= HAND_CONFIDENCE.critical
        ? primary.gesture
        : '无'
      this._updateLegacyControl(legacyGesture)
    }

    if (!wasEnabled && this._controlEnabled) {
      this.cancelPendingGesture('control-enabled')
    }
    if (!this._controlEnabled) {
      this.cancelPendingGesture('control-disabled')
      if (this._gestureCb) this._gestureCb({
        ...pointerFrame,
        code: 0,
        x: this._cursorX,
        y: this._cursorY,
        controlEnabled: false,
        pointerActive: false,
      })
      return
    }

    if ((!preferredTrackable && !alternateTrackable) || primary.gesture === '无') {
      this.cancelPendingGesture('primary-hand-lost')
      if (this._gestureCb) {
        this._gestureCb({
          ...pointerFrame,
          code: 0,
          x: this._cursorX,
          y: this._cursorY,
          controlEnabled: true,
          pointerActive: false,
        })
      }
      return
    }

    const bothHandsActive = Boolean(
      right.is_active && left.is_active
      && right.confidence >= HAND_CONFIDENCE.tracking
      && left.confidence >= HAND_CONFIDENCE.tracking
    )
    const requestedCombined = payload.combined || 'none'
    const combinedThreshold = this._combinedConfidenceThreshold(requestedCombined)
    const combinedConfident = bothHandsActive
      && right.confidence >= combinedThreshold
      && left.confidence >= combinedThreshold
    const combined = combinedConfident ? requestedCombined : 'none'
    const combinedSuppressed = requestedCombined !== 'none' && !combinedConfident
    if (combinedSuppressed) this._emitFeedback('gesture_low_confidence', 800)

    if (this._neutralRequired && combined !== 'none') {
      if (this._gestureCb) {
        this._gestureCb({
          ...pointerFrame,
          code: 0,
          x: this._cursorX,
          y: this._cursorY,
          neutralRequired: true,
          pointerActive: false,
        })
      }
      return
    }

    if (combined !== 'none') {
      if (this._lastCombinedGesture !== combined) {
        this.cancelPendingGesture('combined-mode')
        this._lastCombinedGesture = combined
        this._combinedActionFired = false
        this._exitArmed = true
      }

      const rightSX = this._screenX(right.x, payload.coordinate_space)
      const rightSY = parseFloat(right.y) * window.innerHeight
      const leftSX = this._screenX(left.x, payload.coordinate_space)
      const leftSY = parseFloat(left.y) * window.innerHeight
      const midX = (rightSX + leftSX) / 2
      const midY = (rightSY + leftSY) / 2

      if (combined === 'scale_drag') {
        const now = Date.now()
        if (!this._twoHandInited) {
          this._twoHandCursorX = midX
          this._twoHandCursorY = midY
          this._twoHandInited = true
        } else {
          this._twoHandCursorX += (midX - this._twoHandCursorX) * 0.35
          this._twoHandCursorY += (midY - this._twoHandCursorY) * 0.35
        }
        this._targetX = this._twoHandCursorX
        this._targetY = this._twoHandCursorY
        this._cursorX = this._targetX
        this._cursorY = this._targetY
        this._isSnapped = false

        if (!this._scaleDragCandidateSince) {
          this._scaleDragCandidateSince = now
          this._emitFeedback('scale_drag_arming', 0)
        }
        const dwellProgress = Math.min(
          1,
          (now - this._scaleDragCandidateSince) / this._scaleDragDwellMs
        )
        if (!this._scaleDragLocked && dwellProgress >= 1) {
          this._scaleDragLocked = true
          this._resetTwoHandMotion()
          this._emitAction('scale_drag')
        }

        if (this._scaleDragLocked && this._isPointInCesium(midX, midY)) {
          this._handleScaleDragContinuous(rightSX, rightSY, leftSX, leftSY)
        } else if (this._scaleDragLocked) {
          this._resetTwoHandMotion()
        }

        this._refreshCursorActivity()
        this._currentGesture = 9
        this._gestureName = '缩放拖拽'
        if (this._gestureCb) {
          this._gestureCb({
            ...pointerFrame,
            code: 9,
            x: this._cursorX,
            y: this._cursorY,
            confidence: Math.min(right.confidence, left.confidence),
            arming: !this._scaleDragLocked,
            dwellProgress,
            pointerActive: false,
          })
        }
        return
      } else if (combined === 'drag') {
        // 旧协议的单捏合 drag 没有可靠的按下/释放生命周期，安全起见忽略。
        this.cancelPendingGesture('legacy-drag-rejected')
        return
      } else {
        const cm = COMBINED_MAP[combined] || { code: 0, name: combined }
        this._handleCombinedAction(cm.code, combined)
      }

      this._refreshCursorActivity()
      const cm = COMBINED_MAP[combined] || { code: 0, name: combined }
      this._currentGesture = cm.code
      this._gestureName = cm.name
      if (this._gestureCb) this._gestureCb({
        ...pointerFrame,
        code: cm.code,
        x: this._cursorX,
        y: this._cursorY,
        pointerActive: false,
      })
      return
    } else if (this._lastCombinedGesture !== 'none') {
      this._resetCombinedState()
    }

    this._refreshCursorActivity()

    const rawX = this._screenX(primary.x, payload.coordinate_space)
    const rawY = parseFloat(primary.y) * window.innerHeight

    const mapped = GESTURE_MAP[primary.gesture] || GESTURE_MAP_EN[primary.gesture] || { code: 0, name: '' }
    const confidenceTier = this._confidenceTier(primary.confidence)
    const actionAllowed = confidenceTier === 'action' && !combinedSuppressed
    const gesture = actionAllowed || mapped.code === 1 ? mapped.code : 1
    if (!actionAllowed && mapped.code > 1) {
      this._emitFeedback('gesture_low_confidence', 800)
    }

    const dist = Math.hypot(rawX - this._targetX, rawY - this._targetY)
    if (dist >= 3) {
      let factor = this._baseSmoothing + (dist / 200) * (this._maxSmoothing - this._baseSmoothing)
      factor = Math.min(factor, this._maxSmoothing)
      this._targetX = this._lerp(this._targetX, rawX, factor)
      this._targetY = this._lerp(this._targetY, rawY, factor)
    }

    const physics = this._calculateMagnetism(this._targetX, this._targetY)
    this._cursorX = physics.x
    this._cursorY = physics.y
    this._isSnapped = physics.snapped
    this._updateHoverState(this._cursorX, this._cursorY)

    if (this._neutralRequired) {
      if (mapped.code === 1 && primary.confidence >= HAND_CONFIDENCE.neutral) {
        if (!this._neutralSince) this._neutralSince = Date.now()
        if (Date.now() - this._neutralSince >= this._neutralHoldMs) {
          this._neutralRequired = false
          this._pinchArmed = true
        }
      } else {
        this._neutralSince = 0
      }
      if (this._gestureCb) {
        this._gestureCb({
          ...pointerFrame,
          code: gesture,
          x: this._cursorX,
          y: this._cursorY,
          confidence: primary.confidence,
          confidenceTier,
          neutralRequired: true,
        })
      }
      return
    }

    if (gesture === 5) {
      if (!this._isScrolling) {
        this._isScrolling = true
        this._scrollStartY = rawY
        this._scrollLastY = rawY
      } else {
        const deltaY = rawY - this._scrollLastY
        if (Math.abs(deltaY) > 1) this._scrollAtPoint(this._cursorX, this._cursorY, deltaY * 2.0)
        this._scrollLastY = rawY
      }
    } else {
      this._isScrolling = false
    }

    if (this._currentGesture !== gesture) {
      this._currentGesture = gesture
      const names = {
        0: '', 1: '', 2: '点击', 3: '返回', 4: '握拳', 5: '滚动',
        6: '关闭手势识别', 7: '开启手势识别', 8: '确认',
        9: '缩放拖拽', 10: '退出系统', 11: '语音激活'
      }
      this._gestureName = names[gesture] || ''
    }

    if (this._gestureCb) {
      this._gestureCb({
        ...pointerFrame,
        code: gesture,
        x: this._cursorX,
        y: this._cursorY,
        confidence: primary.confidence,
        confidenceTier,
        suppressedGesture: actionAllowed ? '' : primary.gesture,
      })
    }
    if (actionAllowed || mapped.code <= 1) {
      this._handleAction(gesture)
    } else {
      this._handleSuppressedAction(mapped.code)
    }
  }

  _refreshCursorActivity() {
    this._isCursorActive = true
    if (this._cursorIdleTimer) clearTimeout(this._cursorIdleTimer)
    this._cursorIdleTimer = setTimeout(() => { this._isCursorActive = false }, 3000)
  }

  _lerp(a, b, n) { return (1 - n) * a + n * b }

  _calculateMagnetism(x, y) {
    const selector = this._magneticSelectors.join(',')
    if (!selector) return { x, y, snapped: false }
    const els = document.querySelectorAll(selector)
    let minD = Infinity, closest = null
    els.forEach(el => {
      if (!el || typeof el.closest !== 'function') return
      const r = el.getBoundingClientRect()
      if (r.width === 0 || r.height === 0 || r.bottom < 0 || r.top > window.innerHeight) return
      let cx = r.left + r.width / 2, cy = r.top + r.height / 2
      let d = Math.hypot(x - cx, y - cy)
      if (d < this._snapRadius && d < minD) { minD = d; closest = { cx, cy } }
    })
    if (closest) {
      let strength = Math.pow(1 - (minD / this._snapRadius), 2)
      return { x: this._lerp(x, closest.cx, strength * 0.6), y: this._lerp(y, closest.cy, strength * 0.6), snapped: true }
    }
    return { x, y, snapped: false }
  }

  _updateHoverState(x, y) {
    const el = this._elementFromPoint(x, y)

    if (el !== this._lastHoveredEl) {
      if (this._lastHoveredEl) this._dispatchEvent(this._lastHoveredEl, ['mouseleave', 'mouseout'], x, y)
      if (el) this._dispatchEvent(el, ['mouseenter', 'mouseover'], x, y)
      this._lastHoveredEl = el
    } else if (el) {
      this._dispatchEvent(el, ['mousemove'], x, y)
    }
  }

  _dispatchEvent(target, types, x, y) {
    types.forEach(k => target.dispatchEvent(new MouseEvent(k, { view: window, bubbles: true, clientX: x, clientY: y })))
  }

  _elementFromPoint(x, y) {
    const cursorEl = document.querySelector('.virtual-cursor')
    const previousPointerEvents = cursorEl ? cursorEl.style.pointerEvents : ''
    if (cursorEl) cursorEl.style.pointerEvents = 'none'
    try {
      return document.elementFromPoint(x, y)
    } finally {
      if (cursorEl) cursorEl.style.pointerEvents = previousPointerEvents
    }
  }

  _updateLegacyControl(rawGesture, now = Date.now()) {
    const mapped = GESTURE_MAP[rawGesture] || GESTURE_MAP_EN[rawGesture]
    const code = mapped ? mapped.code : 0
    if (code !== 6 && code !== 7) {
      this._legacyControlCandidate = 0
      this._legacyControlSince = 0
      return
    }

    const desiredEnabled = code === 7
    if (desiredEnabled === this._controlEnabled) {
      this._legacyControlCandidate = 0
      this._legacyControlSince = 0
      return
    }
    if (this._legacyControlCandidate !== code) {
      this._legacyControlCandidate = code
      this._legacyControlSince = now
      return
    }

    const requiredHoldMs = code === 6 ? 1000 : 300
    if (now - this._legacyControlSince >= requiredHoldMs) {
      this._controlEnabled = desiredEnabled
      this._legacyControlCandidate = 0
      this._legacyControlSince = 0
    }
  }

  cancelPendingGesture(reason = 'cancelled') {
    const requireNeutral = reason !== 'combined-mode'
    if (this._gestureTimer) clearTimeout(this._gestureTimer)
    if (this._fistGraceTimer) clearTimeout(this._fistGraceTimer)
    if (this._clickVisualTimer) clearTimeout(this._clickVisualTimer)
    if (this._scrollTimer) clearTimeout(this._scrollTimer)
    this._gestureTimer = null
    this._fistGraceTimer = null
    this._clickVisualTimer = null
    this._scrollTimer = null
    this._fistLock = false
    this._isClicking = false
    this._isScrolling = false
    this._scrollAccumY = 0
    this._lastActionGesture = 0
    this._pinchArmed = false
    this._resetCombinedState(false)

    if (reason === 'disconnect' || reason === 'connection-lost') {
      this._legacyControlCandidate = 0
      this._legacyControlSince = 0
    }

    if (requireNeutral) {
      if (this._cursorIdleTimer) clearTimeout(this._cursorIdleTimer)
      this._cursorIdleTimer = null
      this._isCursorActive = false
    }

    if (this._lastHoveredEl) {
      try {
        this._dispatchEvent(
          this._lastHoveredEl,
          ['mouseleave', 'mouseout'],
          this._cursorX,
          this._cursorY
        )
      } catch (_) {}
      this._lastHoveredEl = null
    }

    if (requireNeutral) {
      this._neutralRequired = true
      this._neutralSince = 0
    }
  }

  _handleAction(g) {
    const now = Date.now()
    const previousGesture = this._lastActionGesture
    this._lastActionGesture = g

    // ── 捏合上升沿 → 单次点击；必须松开后才能再次触发 ──
    if (g === 2) {
      this._isClicking = true
      if (previousGesture !== 2 && this._pinchArmed && now - this._lastClickTime >= 180) {
        console.log('[Gesture] 触发点击', { x: this._cursorX, y: this._cursorY })
        this._triggerClick(this._cursorX, this._cursorY)
        this._lastClickTime = now
        this._pinchArmed = false
      }
      if (this._clickVisualTimer) clearTimeout(this._clickVisualTimer)
      this._clickVisualTimer = setTimeout(() => {
        this._isClicking = false
        this._clickVisualTimer = null
      }, 200)
    } else if (previousGesture === 2) {
      this._pinchArmed = true
      this._isClicking = false
    }

    // ── 挥手 → 返回/关闭 ──
    if (g === 3 && previousGesture !== 3) {
      if (now - this._lastByeTime > 600) {
        this._lastByeTime = now
        console.log('[Gesture] 触发返回 bye')
        this._emitAction('bye')
      }
    }

    // ── 握拳 → 长按500ms唤醒语音 (带容错窗口) ──
    if (g === 4) {
      // 已锁定（已触发），忽略后续
      if (this._fistLock) return

      // 如果有容错窗口在进行中，不要重新启动计时器
      if (this._fistGraceTimer) {
        clearTimeout(this._fistGraceTimer)
        this._fistGraceTimer = null
        // 计时器继续运行，不重新启动
        return
      }

      // 启动新的握拳长按计时器
      if (!this._gestureTimer) {
        console.log('[Gesture] 握拳计时开始 (500ms)')
        this._gestureTimer = setTimeout(() => {
          console.log('[Gesture] 触发 fist_hold')
          this._emitAction('fist_hold')
          this._fistLock = true
          this._gestureTimer = null
        }, 500)
      }
    } else {
      // ── 非握拳手势: 启动容错窗口而非立即清除 ──
      if (this._gestureTimer && !this._fistGraceTimer) {
        // 短暂离开握拳时，给一个容错窗口
        this._fistGraceTimer = setTimeout(() => {
          // 容错窗口到期，手势确实离开了握拳
          if (this._gestureTimer) { clearTimeout(this._gestureTimer); this._gestureTimer = null }
          this._fistGraceTimer = null
          console.log('[Gesture] 握拳容错窗口到期，清除计时器')
        }, this._fistGraceMs)
      } else if (!this._gestureTimer) {
        // 没有活跃的握拳计时器，直接清理
        if (this._fistGraceTimer) { clearTimeout(this._fistGraceTimer); this._fistGraceTimer = null }
      }
      // 重置锁定（允许下次握拳再次触发）
      if (this._fistLock && g !== 4) {
        this._fistLock = false
        console.log('[Gesture] 握拳锁定已重置')
      }
    }

    // ── 胜利(V字) → 系统关闭手势 (后端处理, 前端不触发动作) ──
    // 胜利手势在 _handleMessage 中已被拦截, 不会进入 _handleAction

    // ── 大拇指朝上 → 确认 (开启由后端处理) ──
    if (g === 7 && previousGesture !== 7) {
      if (now - this._lastConfirmTime > 600) {
        this._lastConfirmTime = now
        console.log('[Gesture] 触发 confirm_send')
        this._emitAction('confirm_send')
      }
    }

    // ── OK手势 → 确认（语音启停交给握拳/双手手势） ──
    if (g === 8 && previousGesture !== 8) {
      if (now - this._lastConfirmTime > 600) {
        this._lastConfirmTime = now
        console.log('[Gesture] 触发 confirm (OK)')
        this._emitAction('confirm')
      }
    }
  }

  _handleSuppressedAction() {
    // 置信度暂时下降不等于用户已经松开手势。保留上一次动作锁，避免
    // 持续捏合/OK/挥手在置信度恢复后被当成新的上升沿再次触发。
    this._isClicking = false

    // 握拳尚未达到长按时间时不能在低置信度期间继续计时；沿用短暂
    // 容错窗口，持续不确定则取消，恢复后可重新稳定计时。
    if (this._gestureTimer && !this._fistGraceTimer) {
      this._fistGraceTimer = setTimeout(() => {
        if (this._gestureTimer) {
          clearTimeout(this._gestureTimer)
          this._gestureTimer = null
        }
        this._fistGraceTimer = null
      }, this._fistGraceMs)
    }
  }

  _handleCombinedAction(code, name) {
    if (this._combinedActionFired) return

    // 双手组合先驻留再触发：语音激活 0.4s，高影响的退出动作 1.5s。
    if ((code === 10 || code === 11)
        && (code !== 10 || this._exitArmed)
        && !this._exitHoldTimer) {
      const holdMs = code === 10 ? this._exitHoldMs : this._voiceActivateHoldMs
      this._exitHoldTimer = setTimeout(() => {
        this._exitHoldTimer = null
        if (this._lastCombinedGesture !== name || !this._controlEnabled) return
        this._combinedActionFired = true
        if (code === 10) {
          this._exitArmed = false
          console.log('[Gesture] 双手联动: 长持后退出系统')
          this._emitAction('exit_system')
        } else {
          console.log('[Gesture] 双手联动: 稳定后语音激活')
          this._emitAction('voice_activate')
        }
      }, holdMs)
    }
  }

  _handleScaleDragContinuous(rightSX, rightSY, leftSX, leftSY) {
    // 双手缩放/旋转/俯仰采用“主导自由度”，一帧只执行一种变换，
    // 避免拉近地图时同时意外旋转和仰俯。
    if (!this._cesiumViewer || !this._cesium3DEnabled) return

    const handDist = Math.hypot(rightSX - leftSX, rightSY - leftSY)
    const midX = (rightSX + leftSX) / 2
    const midY = (rightSY + leftSY) / 2

    try {
      const camera = this._cesiumViewer.camera
      let ratio = 1.0
      if (this._lastScaleDist > 0) {
        const rawRatio = handDist / this._lastScaleDist
        this._emaScaleRatio = this._emaScaleRatio * 0.7 + rawRatio * 0.3
        ratio = this._emaScaleRatio
      }

      let dx = 0, dy = 0
      if (this._midInited) {
        dx = midX - this._lastMidX
        dy = midY - this._lastMidY
      }
      this._lastMidX = midX
      this._lastMidY = midY
      this._midInited = true

      this._emaDx = this._emaDx * 0.5 + dx * 0.5
      this._emaDy = this._emaDy * 0.5 + dy * 0.5

      const intent = this._normalizedTwoHandIntent(ratio, this._emaDx, this._emaDy)
      this._updateTwoHandMode(intent, Date.now())

      if (this._twoHandMode === 'scale' && intent.scale > 0) {
        const height = camera.positionCartographic.height
        const safeRatio = Math.max(0.94, Math.min(1.06, ratio))
        const deltaHeight = height * (1 - 1 / safeRatio) * 0.25
        if (Math.abs(deltaHeight) > 0.5) {
          const upRadial = Cesium.Cartesian3.normalize(
            camera.position, new Cesium.Cartesian3()
          )
          camera.move(upRadial, -deltaHeight)
        }
      } else if (this._twoHandMode === 'rotate' && intent.rotate > 0) {
        const headingDelta = Math.max(-30, Math.min(30, this._emaDx)) * 0.003
        camera.setView({
          destination: camera.position,
          orientation: {
            heading: camera.heading + headingDelta,
            pitch: camera.pitch,
            roll: camera.roll,
          },
        })
      } else if (this._twoHandMode === 'tilt' && intent.tilt > 0) {
        let newPitch = camera.pitch - Math.max(-30, Math.min(30, this._emaDy)) * 0.002
        newPitch = Math.max(-Math.PI / 2 + 0.05, Math.min(-0.05, newPitch))
        camera.pitch = newPitch
      }

      this._lastScaleDist = handDist
      this._isScaleDragging = true
    } catch (e) {
      console.warn('[Gesture] Cesium 双手操作失败:', e)
    }
  }

  _normalizedTwoHandIntent(ratio, dx, dy) {
    const width = Math.max(1, window.innerWidth)
    const height = Math.max(1, window.innerHeight)
    const magnitudes = {
      scale: Math.abs(ratio - 1.0),
      rotate: Math.abs(dx) / width,
      tilt: Math.abs(dy) / height,
    }
    const scores = {}
    Object.keys(magnitudes).forEach(key => {
      const deadzone = TWO_HAND_DEADZONE[key]
      scores[key] = magnitudes[key] <= deadzone
        ? 0
        : (magnitudes[key] - deadzone) / deadzone
    })
    return scores
  }

  _updateTwoHandMode(intent, now = Date.now()) {
    const modes = ['scale', 'rotate', 'tilt']
    const dominant = modes.reduce(
      (best, key) => (intent[key] || 0) > (intent[best] || 0) ? key : best,
      'scale'
    )
    const dominantScore = intent[dominant] || 0

    if (dominantScore <= 0) {
      this._twoHandModeCandidate = 'idle'
      this._twoHandModeCandidateSince = 0
      if (!this._twoHandIdleSince) this._twoHandIdleSince = now
      if (now - this._twoHandIdleSince >= this._twoHandModeDwellMs) {
        this._twoHandMode = 'idle'
      }
      return this._twoHandMode
    }
    this._twoHandIdleSince = 0

    if (this._twoHandMode === dominant) {
      this._twoHandModeCandidate = 'idle'
      this._twoHandModeCandidateSince = 0
      return this._twoHandMode
    }
    if (
      this._twoHandMode !== 'idle'
      && now - this._twoHandModeLockedAt < this._twoHandModeMinLockMs
    ) {
      return this._twoHandMode
    }

    const currentScore = intent[this._twoHandMode] || 0
    if (this._twoHandMode !== 'idle' && dominantScore < Math.max(0.5, currentScore * 1.35)) {
      return this._twoHandMode
    }
    if (this._twoHandModeCandidate !== dominant) {
      this._twoHandModeCandidate = dominant
      this._twoHandModeCandidateSince = now
      return this._twoHandMode
    }
    if (now - this._twoHandModeCandidateSince >= this._twoHandModeDwellMs) {
      this._twoHandMode = dominant
      this._twoHandModeLockedAt = now
      this._twoHandModeCandidate = 'idle'
      this._twoHandModeCandidateSince = 0
      this._emitFeedback(`3d_mode_${dominant}`, 300)
    }
    return this._twoHandMode
  }

  _resetTwoHandMotion() {
    this._lastScaleDist = 0
    this._emaScaleRatio = 1.0
    this._lastMidX = 0
    this._lastMidY = 0
    this._midInited = false
    this._emaDx = 0
    this._emaDy = 0
    this._twoHandMode = 'idle'
    this._twoHandModeCandidate = 'idle'
    this._twoHandModeCandidateSince = 0
    this._twoHandModeLockedAt = 0
    this._twoHandIdleSince = 0
    this._isScaleDragging = false
  }

  _resetCombinedState(requireNeutral = true) {
    const scaleWasPending = this._lastCombinedGesture === 'scale_drag'
      && Boolean(this._scaleDragCandidateSince)
    const scaleWasLocked = scaleWasPending && this._scaleDragLocked
    if (this._exitHoldTimer) clearTimeout(this._exitHoldTimer)
    this._exitHoldTimer = null
    this._exitArmed = true
    this._combinedActionFired = false
    this._lastCombinedGesture = 'none'
    this._twoHandInited = false
    this._scaleDragCandidateSince = 0
    this._scaleDragLocked = false
    this._resetTwoHandMotion()
    if (scaleWasPending) {
      this._emitFeedback(scaleWasLocked ? 'scale_drag_ended' : 'scale_drag_cancelled', 0)
    }
    if (requireNeutral) {
      this._neutralRequired = true
      this._neutralSince = 0
      this._pinchArmed = false
    }
  }

  _isPointInCesium(x, y) {
    const container = document.getElementById && document.getElementById('cesiumContainer')
    const canvas = this._cesiumViewer && this._cesiumViewer.canvas
    const target = container || canvas
    if (!target || typeof target.getBoundingClientRect !== 'function') return false
    const rect = target.getBoundingClientRect()
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  _scrollAtPoint(x, y, deltaY) {
    const target = this._elementFromPoint(x, y)
    let node = target
    while (node && node !== document.body && node !== document.documentElement) {
      const canScroll = node.scrollHeight > node.clientHeight
      const style = window.getComputedStyle ? window.getComputedStyle(node) : null
      const overflowY = style ? style.overflowY : ''
      if (canScroll && (!style || /auto|scroll|overlay/.test(overflowY))) {
        const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight)
        const current = Number(node.scrollTop) || 0
        const canMove = deltaY < 0 ? current > 0 : current < maxScroll
        if (canMove) {
          if (typeof node.scrollBy === 'function') node.scrollBy(0, deltaY)
          else node.scrollTop = Math.max(0, Math.min(maxScroll, current + deltaY))
          return true
        }
      }
      node = node.parentElement
    }

    const root = document.scrollingElement || document.documentElement || document.body
    const rootHeight = Number(root && root.scrollHeight) || 0
    const viewportHeight = Number(window.innerHeight) || Number(root && root.clientHeight) || 0
    const rootMax = Math.max(0, rootHeight - viewportHeight)
    const rootTop = Number(root && root.scrollTop) || Number(window.scrollY) || 0
    const rootStyle = root && window.getComputedStyle ? window.getComputedStyle(root) : null
    const rootOverflow = rootStyle ? rootStyle.overflowY : ''
    const rootCanMove = rootMax > 0
      && !/hidden|clip/.test(rootOverflow)
      && (deltaY < 0 ? rootTop > 0 : rootTop < rootMax)
    if (rootCanMove && typeof window.scrollBy === 'function') {
      window.scrollBy(0, deltaY)
      return true
    }
    this._emitFeedback('scroll_unavailable', 700)
    return false
  }

  _emitFeedback(action, cooldownMs = 600) {
    const now = Date.now()
    const previous = this._feedbackTimes.get(action) || 0
    if (cooldownMs > 0 && now - previous < cooldownMs) return
    this._feedbackTimes.set(action, now)
    this._emitAction(action)
  }

  _emitAction(action) {
    // 向所有注册的动作回调广播
    this._actionCbs.forEach(cb => {
      try { cb(action) } catch (e) { console.warn('[Gesture] action callback error:', e) }
    })
  }

  _triggerClick(x, y) {
    const el = this._elementFromPoint(x, y)
    if (!el) return
    console.log('[Gesture] 点击目标:', el.tagName, el.className, el.id)

    // 先尝试磁性吸附目标
    const selector = this._magneticSelectors.join(',')
    let target = selector && typeof el.closest === 'function'
      ? el.closest(selector)
      : null
    if (!target) target = el

    // 处理 el-select
    const selectRoot = target.classList && target.classList.contains('el-select')
      ? target
      : (typeof target.closest === 'function' ? target.closest('.el-select') : null)
    if (selectRoot) {
      const input = typeof selectRoot.querySelector === 'function'
        ? selectRoot.querySelector('input')
        : null
      if (input) target = input
    }

    // 只派发一个 click；原先 click() + dispatchEvent() 会使一次捏合执行两次。
    try { target.focus() } catch(e) {}
    target.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true, clientX: x, clientY: y }))
  }

  setMagnetismSelectors(selectors) { this._magneticSelectors = [...selectors] }

  bindCesiumViewer(viewer) { this._cesiumViewer = viewer }
  enable3DControl(bool) { this._cesium3DEnabled = bool }

  onGesture(cb) {
    if (typeof cb !== 'function') return () => {}
    this._gestureCb = cb
    return () => this.offGesture(cb)
  }
  offGesture(cb) {
    if (cb === undefined || this._gestureCb === cb) this._gestureCb = null
  }
  onAction(cb) {
    if (typeof cb !== 'function') return () => {}
    if (!this._actionCbs.includes(cb)) this._actionCbs.push(cb)
    return () => this.offAction(cb)
  }
  offAction(cb) { this._actionCbs = this._actionCbs.filter(c => c !== cb) }

  get cursorX() { return this._cursorX }
  get cursorY() { return this._cursorY }
  get cursorStyle() {
    return {
      left: this._cursorX + 'px',
      top: this._cursorY + 'px',
      transition: 'none'
    }
  }
  get cursorClass() {
    return {
      clicking: this._isClicking,
      scrolling: this._isScrolling,
      snatched: this._isSnapped,
      snapped: this._isSnapped,
      fisting: this._currentGesture === 4,
      inactive: !this._isCursorActive || !this._controlEnabled,
      hidden: !this._controlEnabled,
      controlEnabled: this._controlEnabled
    }
  }
  get isConnected() { return this._isConnected }
  get controlEnabled() { return this._controlEnabled }
  get combinedGesture() { return this._lastCombinedGesture }

  start() {}
  stop() { this.disconnect() }
}
